import { Formik } from "formik";
import React, { FC, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PatientInformationDrawer from "../PatientInformationDrawer";
import useEditMode from "./useEditMode";
import useNormalizedPatientDrawerData from "./useNormalizedPatientDrawerData";
import usePatientData from "./usePatientData";
import usePatientFormik from "./usePatientFormik";
import { useAppContext } from "../../Unknown/AppContext";

export type PatientInformationDrawerContainerProps = {
  patientMrn?: string;
  isOpen: boolean;
  handleClose: () => void;
  handleCustomerClick: (customerId: string) => void;
  handleAppointmentClick: (customerId: string) => void;
  handlePostSubmit?: () => void | Promise<void>;
  // Both set by the merge screens, which render this drawer as an overlay:
  // the in-drawer "Merge" action makes no sense (and would navigate to
  // `<current>/merge`, i.e. `.../merge/merge`) while a merge is in progress,
  // and an archived patient must be swapped in place rather than via the URL.
  isMergeActionShown?: boolean;
  onMergeRedirect?: (targetMrn: string) => void;
};

const MERGE_ALLOWED_ROLES = ["regional_admin", "super_admin"] as const;

const PatientInformationDrawerContainer: FC<
  PatientInformationDrawerContainerProps
> = ({
  patientMrn,
  isOpen,
  handleClose,
  handleCustomerClick,
  handlePostSubmit,
  handleAppointmentClick,
  isMergeActionShown = true,
  onMergeRedirect,
}) => {
  const {
    isLoading,
    error,
    data: patient,
    refetch,
  } = usePatientData(patientMrn, onMergeRedirect);

  const navigate = useNavigate();
  const location = useLocation();
  const { databaseUserData } = useAppContext();
  const canMerge = MERGE_ALLOWED_ROLES.some((role) =>
    databaseUserData.permissions.includes(role),
  );
  const handleMergeClick = useCallback(() => {
    navigate(`${location.pathname}/merge`);
  }, [location.pathname, navigate]);

  const isPatientContactFieldsShown = useMemo(() => {
    if (!patient) return false;
    return !patient.legal_guardian_id && !patient.family_contact_id;
  }, [patient]);

  const normalizedPatientData = useNormalizedPatientDrawerData({
    patient,
    handleCustomerClick,
    handleAppointmentClick,
    isEmailShown: isPatientContactFieldsShown,
    isPhoneShown: isPatientContactFieldsShown,
  });

  const { isEditMode, handleEditModeChange } = useEditMode();

  const onSubmitSuccess = useCallback(async () => {
    await refetch();

    handleEditModeChange(false);

    if (handlePostSubmit) await handlePostSubmit();
  }, [handleEditModeChange, handlePostSubmit, refetch]);

  const { formikConfig: patientFormikConfig } = usePatientFormik({
    patient,
    isEmailShown: isPatientContactFieldsShown,
    isPhoneShown: isPatientContactFieldsShown,
    onSubmitSuccess,
  });

  return (
    <Formik {...patientFormikConfig}>
      <PatientInformationDrawer
        isOpen={isOpen}
        handleClose={handleClose}
        isLoading={isLoading}
        error={error}
        data={normalizedPatientData}
        isEditMode={isEditMode}
        handleEditModeChange={handleEditModeChange}
        canMerge={canMerge && isMergeActionShown}
        onMergeClick={handleMergeClick}
      />
    </Formik>
  );
};

export default PatientInformationDrawerContainer;
